import { getRetailerUrl, RouteName, routes } from '@shared/constants/routes'
import { createServerSidePropsHandler } from '@/features/shared/serverUtils/ssrHelpers/createServerSidePropsHandler'
import { CMSPage, type CMSPageProps } from '@/features/cms/components/CMS/CMSPage'
import { fetchCMSDataForSSR } from '@/features/cms/services/CMS/serverUtils'
import { Circle360Header } from '@/features/shared/components/Headers/Circle360Header'
import {
  getPageRedirect,
  RedirectReason,
} from '@/features/shared/serverUtils/ssrHelpers/redirectReason'
import { INFERRED_ZIP, INFERRED_ZIP_SRC } from '@shared/constants/zip'
import { CMS_UTM_SOURCE } from '@/features/entrypoint/components/Circle360/constants'
import ServerCookies from 'cookies'
import { getFiveDigitZipcode } from '@/features/account/services/Addresses/utils'
import { secureClientOptions } from '@shared/constants/cookies'
import { removeServerGuestCookies } from '@/features/shared/serverUtils/guest'
import { getGuestStoreParamsSSR } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { attachLayout } from '@/features/shared/components/Headers/Layouts/attachLayout'

export const getServerSideProps = createServerSidePropsHandler(
  RouteName.TARGET_CIRCLE_360,
  async (pageDataParams) => {
    const {
      ssrContext,
      authContext: { isAuthenticated, isOnboarded },
    } = pageDataParams
    const cookies = ssrContext.req.cookies

    const { zip_code, utm_source, retailer_name } = ssrContext.query
    const zip = getFiveDigitZipcode(zip_code)
    const serverCookies = new ServerCookies(ssrContext.req, ssrContext.res)
    if (zip) {
      const { guestAddress } = getGuestStoreParamsSSR(cookies)
      serverCookies.set(INFERRED_ZIP, zip, {
        ...secureClientOptions,
        maxAge: 0,
      })
      serverCookies.set(INFERRED_ZIP_SRC, 't', {
        ...secureClientOptions,
        maxAge: 0,
      })

      if (guestAddress && guestAddress.zip_code !== zip) {
        removeServerGuestCookies(ssrContext.req, ssrContext.res)
      }
    }
    if (utm_source) {
      serverCookies.set(CMS_UTM_SOURCE, String(utm_source), {
        ...secureClientOptions,
        maxAge: 0,
      })
    }

    if (isAuthenticated && !isOnboarded && !zip) {
      return getPageRedirect(routes.WELCOME.url, RedirectReason.NOT_ONBOARDED)
    }

    // Authenticated users always skip the landing page: Target Order
    // Confirmation traffic (retailer_name + zip_code) goes to the RHP,
    // everyone else to the GHP.
    if (isAuthenticated) {
      if (retailer_name && zip) {
        return getPageRedirect(
          getRetailerUrl(String(retailer_name)),
          RedirectReason.ALREADY_AUTHENTICATED,
        )
      }

      return getPageRedirect(routes.GLOBAL_HOMEPAGE.url, RedirectReason.ALREADY_AUTHENTICATED)
    }

    // Unauthenticated traffic with a retailer_name is routed through Target
    // SSO, returning to the RHP when a zip_code is present or the GHP
    // otherwise. Without a retailer_name the user stays on the C360 LP.
    if (retailer_name) {
      const loginDestination = zip
        ? `${routes.LOGIN.url}?provider=target&returnTo=${getRetailerUrl(String(retailer_name))}`
        : `${routes.LOGIN.url}?provider=target`
      return getPageRedirect(loginDestination, RedirectReason.AUTH_REQUIRED)
    }

    const cmsData = await fetchCMSDataForSSR(pageDataParams, zip)

    return {
      props: {
        cmsData: cmsData.props.cmsData,
        useHeaderLayout: true,
        hideChat: true,
      } satisfies CMSPageProps,
    }
  },
)

export default attachLayout(CMSPage, (page) => (
  <>
    <Circle360Header />
    {page}
  </>
))
